// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import camelize from 'camelize2';
import { zooplaPublicAPIFetch } from '@zoopla/data/core';
import { navigation } from '@zoopla/data/navigation';
import { dtoMapper } from './dtoMapper';
import isEmpty from 'lodash.isempty';
import isPlainObject from 'lodash.isplainobject';
import transform from 'lodash.transform';

const landingPageRegEx = /^\/(to-rent|for-sale|new-homes)\/map\/?(\?.*)?$/;
const knownSegmentRegex = new RegExp(
    '^(' +
        'apartments|auction|barn-conversions|browse|bungalows|business|commercial|commercial|commercial-agents|cottages|' +
        'detached|details|developers|developments|discover|estate-agents|farms-land|find-agents|flats|for-sale|ground-floor|' +
        'home-values|hospitality|house-prices|houses|industrial-sites|land|letting-agents|map|market|new-homes|offices|' +
        'overseas|overseas|overseas-agents|' +
        'property|retail-premises|retirement|semi-detached|serviced-offices|studio|studio-flats|terraced|to-rent|' +
        '[1-4]-bedroom(s?)' +
        ')$'
);

const isMapSearchLandingPage = (path: string) => {
    return landingPageRegEx.test(path);
};

const isKnownPathSegment = (segment: string): boolean => {
    return segment.match(knownSegmentRegex) != null;
};

export const getSearchLocationFromPath = (path?: string): string => {
    if (!path) {
        return '';
    }

    if (isMapSearchLandingPage(path)) {
        return 'uk';
    }

    /*
Facts:
  We know the geo identifier is the last few segments of the path.
  We know after last "known" path segment from the beginning is the start of the geo identifier
*/

    // just the segments of the path
    const pathSegments = path
        .split('?')[0] // just the portion before the query character
        .replace(/^\/+/, '') // removing any lead /
        .replace(/\/+$/, '') // removing any trailing /
        .split('/'); // splitting on the internal /s

    for (let index = 0; index < pathSegments.length; index++) {
        const segment = pathSegments[index];
        // segment known?
        if (!isKnownPathSegment(segment)) {
            // add the remaining bits together...
            return pathSegments.slice(index).join('/');
        }
    }

    return path;
};

const updateMapLandingPagePath = (path: string) => {
    if (isMapSearchLandingPage(path) === false) {
        return path;
    }

    const [propertyType, queryStringParameters] = path.split('/map');

    return `${propertyType}/map/property/uk${queryStringParameters}`;
};

async function listings(path: string): Promise<[unknown | null, Error | null]> {
    const url = new URL(process.env.LISTING_SEARCH_API);

    url.searchParams.append('uri', updateMapLandingPagePath(path));
    url.searchParams.append('include_listings', 'true');
    url.searchParams.append('include_facet_counts', 'false');
    url.searchParams.append('include_debug', 'false');

    if (!path.includes('/map/')) {
        url.searchParams.append('poi_categories', 'transport');
    }

    const okStatusCodes = [422]; // endpoint returns json on 422
    try {
        const j = await zooplaPublicAPIFetch(
            {
                host: url.host,
                path: `${url.pathname}${url.search}`,
                secret: process.env.LISTINGS_SECRET
            },
            okStatusCodes
        );

        // handle 422 response by generating a GraphQL-like error
        if ('errors' in j) {
            return [
                null,
                {
                    message: '422: Unprocessable Entity',
                    extensions: {
                        code: 'INTERNAL_SERVER_ERROR',
                        response: {
                            url: url.toString(),
                            status: 422,
                            statusText: 'Unprocessable Entity',
                            body: {
                                errors: j.errors
                            }
                        }
                    }
                }
            ];
        }

        const analytics_taxonomy = j.analytics_taxonomy ?? {};
        const analytics_ecommerce = j.analytics_ecommerce ?? {
            impressions: []
        };
        const results = j.results ?? {};
        const pagination = j.pagination ?? {};
        const page_info = j.page_info ?? {};
        const filters = j.filters ?? {};
        const save_and_alerts = j.save_and_alerts ?? {};
        const listing_search_uris = j.listing_search_uris ?? {};
        const canonical_uri = j.canonical_uri ?? {};
        const sort_order = j.sort_order ?? {};
        const seo_blurb = j.seo_blurb ?? {};

        analytics_taxonomy.geo_identifier = getSearchLocationFromPath(path);

        const response = camelize({
            analytics_taxonomy,
            analytics_ecommerce,
            results,
            pagination,
            page_info,
            filters,
            save_and_alerts,
            listing_search_uris,
            canonical_uri,
            sort_order,
            seo_blurb
        });

        return [{ searchResults: dtoMapper(response) }, null];
    } catch (e) {
        return [{}, e];
    }
}

export async function search(
    path: string
): Promise<{ data: unknown; errors: unknown }> {
    const [nav, results] = await Promise.all([
        navigation(path),
        listings(path)
    ]);

    const errors = [];

    if (nav[1]) {
        errors.push(nav[1]);
    }

    if (results[1]) {
        errors.push(results[1]);
    }

    if (errors.length > 0) {
        return { data: null, errors };
    }

    const res = serialized({
        data: { ...nav[0], ...results[0] },
        errors: null
    });
    return res;
}

/* perform some cleanup on the data before we return, remove undefined and unwanted keys */
function serialized(obj: unknown) {
    return cleanDeep(obj, {
        cleanKeys: [
            'pointsOfInterest',
            'contractFeatures',
            'counts',
            'displayAddress',
            'displayType',
            'firstPropertyImage',
            'firstPublishedDate',
            'floorArea',
            'location',
            'numFloorPlans',
            'numPropertyImages',
            'numVideos',
            'numViews',
            'pricing',
            'publicationStatus',
            'shortPriceTitle',
            'statusSummary',
            'listingCount',
            'numBeds',
            'administrationFees',
            'availableFromDate',
            'distanceMiles',
            'lat',
            'lng',
            'smartViewing',
            'search_guid',
            'search_identifier',
            'floor_area_units',
            'expanded_results_count',
            'geo_identifier',
            'mapView',
            'success',
            'floorAreaUnits',
            'searchSource',
            'keywords',
            'robots',
            'areaLabel',
            'areaUri',
            'featuredType',
            'event'
        ],
        coerceVals: {
            content: (v) => (v === false ? 0 : v),
            numberOfVideos: (v) => (v === false ? 0 : v),
            priceTitle: (v) => (v === false ? '' : v),
            highlights: (v) => (v === false ? [] : v),
            listingId: (v) => (isNaN(v) ? v : v.toString()),
            adTargeting: (v) =>
                v
                    ? cleanDeep(v, {
                          cleanKeys: [
                              'geoIdentifier',
                              'expandedResultsCount',
                              'searchGuid',
                              'searchIdentifier'
                          ]
                      })
                    : v
        }
    });
}

function cleanDeep(
    object,
    {
        cleanKeys = [],
        coerceVals = {},
        cleanValues = [],
        emptyArrays = false,
        emptyObjects = true,
        emptyStrings = false,
        NaNValues = false,
        nullValues = false,
        undefinedValues = true
    } = {}
) {
    return transform(object, (result, value, key) => {
        // Exclude specific keys.
        if (cleanKeys.includes(key)) {
            return;
        }

        // coerce specific key vals
        if (key in coerceVals) {
            value = coerceVals[key](value);
        }

        // Recurse into arrays and objects.
        if (Array.isArray(value) || isPlainObject(value)) {
            value = cleanDeep(value, {
                NaNValues,
                cleanKeys,
                coerceVals,
                cleanValues,
                emptyArrays,
                emptyObjects,
                emptyStrings,
                nullValues,
                undefinedValues
            });
        }

        // Exclude specific values.
        if (cleanValues.includes(value)) {
            return;
        }

        // Exclude empty objects.
        if (emptyObjects && isPlainObject(value) && isEmpty(value)) {
            return;
        }

        // Exclude empty arrays.
        if (emptyArrays && Array.isArray(value) && !value.length) {
            return;
        }

        // Exclude empty strings.
        if (emptyStrings && value === '') {
            return;
        }

        // Exclude NaN values.
        if (NaNValues && Number.isNaN(value)) {
            return;
        }

        // Exclude null values.
        if (nullValues && value === null) {
            return;
        }

        // Exclude undefined values.
        if (undefinedValues && value === undefined) {
            return;
        }

        // Append when recursing arrays.
        if (Array.isArray(result)) {
            return result.push(value);
        }

        result[key] = value;
    });
}
